import type { ReactNode } from "react";
import { useTranslations } from "@/lib/useTranslations";

const textareaClassName =
  "min-h-40 w-full resize-y wireframe-border bg-whart-bg px-3 py-2 text-sm leading-relaxed text-whart-text outline-none focus:border-whart-dark disabled:opacity-50 rounded-xl";

interface InternalNotesFieldProps {
  entityId: string;
  value: string;
  disabled?: boolean;
  isPending: boolean;
  saveSpinner: ReactNode;
  error?: string;
  success: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}

export function InternalNotesField({
  entityId,
  value,
  disabled = false,
  isPending,
  saveSpinner,
  error,
  success,
  onChange,
  onBlur,
}: InternalNotesFieldProps) {
  const t = useTranslations("common");
  const fieldId = `internal-notes-${entityId}`;

  return (
    <section className="space-y-1.5">
      <label htmlFor={fieldId} className="text-sm font-semibold text-whart-text">
        {t("internalNotesLabel")}
      </label>
      <p className="text-xs text-neutral-500">{t("internalNotesHint")}</p>
      <div className="relative">
        <textarea
          id={fieldId}
          rows={6}
          value={value}
          disabled={disabled || isPending}
          aria-busy={isPending}
          placeholder={t("internalNotesPlaceholder")}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          className={textareaClassName}
        />
        {isPending ? (
          <span className="pointer-events-none absolute right-3 top-3">
            {saveSpinner}
          </span>
        ) : null}
      </div>
      {!success && error ? (
        <p role="alert" className="text-xs text-whart-urgent">
          {error}
        </p>
      ) : null}
      {success ? (
        <p role="status" className="text-xs text-emerald-700">
          {t("saveInternalNotesSuccess")}
        </p>
      ) : null}
    </section>
  );
}
